<template>
	<div>
        <tooltip :content="$t('jobs.internal_note.title')" position="bottom">
            <simple-button
                look="icon"
                icon="description"
                small
                @click="startEditingNote"
            />
        </tooltip>

		<edit-resource
            v-if="!!form"
            :title="$t('jobs.internal_note.title')"
            :subtitle="$t('jobs.internal_note.sub_title')"
            :form="form"
            @close="stopEditingNote"
            @submit="saveNote"
            title-as-html
            buttons-always-active
            :hide-top-buttons="!canManage"
            :close-instead-of-save="!canManage"
        >
            <div class="flex items-center gap-16 mb-16">
                <select-dropdown
                    :auto-translate="false"
                    class="grow"
                    label="title"
                    :loading="fetchingReasons"
                    :multiple="true"
                    :options="requestReasons"
                    :placeholder="$t('form.dropdown.placeholder')"
                    reference="id"
                    :translatable="true"
                    v-model="form.request_reason_ids"
                >
                    {{ $t('settings.vacancy.reasons.request') }}
                </select-dropdown>
                <select-dropdown
                    :auto-translate="false"
                    class="grow"
                    label="title"
                    :loading="fetchingReasons"
                    :multiple="true"
                    :options="rejectReasons"
                    :placeholder="$t('form.dropdown.placeholder')"
                    reference="id"
                    :translatable="true"
                    v-model="form.reject_reason_ids"
                >
                    {{ $t('settings.vacancy.reasons.reject') }}
                </select-dropdown>
            </div>
            <wysiwyg v-model="form.internal_notes" full-featured container-class="min-w-768" v-if="canManage"/>
            <p v-else v-html="form.internal_notes"></p>
            <div class="mt-16">
                <loading v-if="files === null"/>
                <template v-else>
                    <div class="mb-8" v-if="canManage">
                        <file-upload v-model="uploadedFiles" multiple @input="uploadFile" all-data ref="fileUpload"/>
                    </div>
                    <ul v-if="files.length" class="rounded-6 overflow-hidden">
                        <li v-for="file in files" :key="file.id" class="odd:bg-primary-5 relative">
                            <a
                                :target="extensions.includes(file.extension) ? '_blank' : null"
                                :href="file.url"
                                :download="!extensions.includes(file.extension) ? file.original_name : null"
                                class="group flex items-center p-16 cursor-pointer"
                            >
                                <div class="mr-auto font-semibold group-hover:text-primary trans-ease">{{
                                        file.original_name
                                    }}
                                </div>
                                <div class="pl-16">
                                    <div
                                        class="h-32 w-32 flex items-center justify-center rounded-6 text-primary-80 trans-ease group-hover:text-primary mr-32"
									>
										<svg-icon type="download" class="max-w-16 max-h-16"></svg-icon>
									</div>
								</div>
							</a>

							<div
								v-if="canManage"
								class="absolute right-0 inset-y-0 mr-16 flex items-center pointer-events-none"
							>
								<button
									class="text-primary-80 hover:text-semantic-error cursor-pointer trans-ease pointer-events-auto h-32 w-32 flex items-center justify-center rounded-6"
									@click="confirmDelete(file)"
								>
									<svg-icon class="max-w-16 max-h-16" type="delete"></svg-icon>
								</button>
							</div>
						</li>
					</ul>

					<popup-modal-confirm
						v-if="canManage && deleting"
						:message="$t('general.confirm_delete_subject', {subject: deleting.original_name})"
						:confirmText="$t('general.delete_subject', {subject: $t('general.doc')})"
						:working="performingDeleteRequest"
						@confirm="deleteFile(deleting)"
						@close="cancelDelete"
					></popup-modal-confirm>
				</template>
			</div>
		</edit-resource>
	</div>
</template>

<script>
	import {
		normalizeJson,
		openableFileExtensionsInWebBrowser
	} from "../../shared/helpers";
	import InternalNoteAttachment from "../models/JobInternalNoteAttachment";
	import EditResource from "../components/ui/EditResource.vue";
	import PopupModalConfirm from "./ui/PopupModalConfirm.vue";
	import FileUpload from "../components/forms/FileUpload";
	import Wysiwyg from "../components/forms/Wysiwyg.vue";
	import SvgIcon from "../components/ui/SvgIcon.vue";
	import Loading from "../components/ui/Loading";
	import * as jobs from "../services/JobService";
	import Form from "../etc/forms/Form";
	import Job from "../models/Job";
    import * as jobAttachments from "../services/JobInternalNoteAttachmentService";
    import RoundButton from './ui/RoundButton';
    import TooltipVue from './navigation/Tooltip.vue';
    import SelectDropdown from '../components/forms/SelectDropdown.vue';
    import * as JobReasonService from "../services/JobReasonService";
    import JobReason from "../models/JobReason";
    import SimpleButton from "./ui/SimpleButton.vue";
    import {useRepo} from "pinia-orm";

    export default {
		components: {
            SimpleButton,
			wysiwyg: Wysiwyg,
			"edit-resource": EditResource,
			"svg-icon": SvgIcon,
			"file-upload": FileUpload,
			loading: Loading,
            "popup-modal-confirm": PopupModalConfirm,
            'round-button': RoundButton,
            tooltip: TooltipVue,
            SelectDropdown
		},
		data() {
			return {
				files: null,

				form: null,
                uploadedFiles: [],
                processedFiles: [],

				deleting: null,
				performingDeleteRequest: false,

				extensions: openableFileExtensionsInWebBrowser,

                fetchingReasons: false,
			};
		},
		props: {
			job: Job,
            canManage: Boolean
		},
        computed: {
            requestReasons() {
                return useRepo(JobReason)
                    .where('is_rejected', 0)
                    .get();
            },
            rejectReasons() {
                return useRepo(JobReason)
                    .where('is_rejected', 1)
                    .get();
            },
        },
		methods: {
            fetchJobReasons() {
                this.fetchingReasons = true
                JobReasonService.index()
                    .then(({data}) => {
                        return useRepo(JobReason).save(normalizeJson(data))
                    })
                    .then(() => {
                        this.fetchingReasons = false
                    })
            },
            fetch() {
                return (this.files = useRepo(InternalNoteAttachment)
                    .where("job_id", this.job.id)
                    .get());
            },
            startEditingNote() {
                this.form = new Form([
                    {
                        name: "internal_notes",
                        value: this.job.internal_notes
                    },
                    {
                        name: 'request_reason_ids',
                        value: this.job.request_reason_ids
                    },
                    {
                        name: 'reject_reason_ids',
                        value: this.job.reject_reason_ids
                    },
                ]);
            },
            stopEditingNote() {
                this.form = null;
            },
            saveNote() {
                this.form.submit(data => {
                    return jobs
                        .update(this.job.id, {
                            data: {attributes: data}
                        })
                        .then(result => {
                            return useRepo(Job).save(normalizeJson(result));
                        })
                        .then(inserts => {
                            this.form = null;
                        });
                });
            },
            uploadFile(files) {
                files.forEach(file => {
                    if (this.processedFiles.includes(file.id))
                        return;

                    this.processedFiles.push(file.id);
                    jobAttachments.store(this.job.id, {
                        data: {
                            attributes: {
                                fileId: file.id
                            }
                        }
                    }).then(result => {
                        return useRepo(InternalNoteAttachment).save(normalizeJson(result.attachment));
                    }).then(() => {
                        this.$refs.fileUpload.removeFileByName(file.attributes.name_original);
						return this.fetch();
					})
                })
            },

			confirmDelete(file) {
				this.deleting = file;
			},
			cancelDelete() {
				this.deleting = false;
			},
			deleteFile(file) {
				this.performingDeleteRequest = true;

				jobAttachments
					.destroy(this.job.id, file.id)
					.then(() => {
						return useRepo(InternalNoteAttachment).destroy(file.id);
					})
					.then(() => {
						return this.fetch();
					})
					.then(() => {
						this.performingDeleteRequest = false;
						this.deleting = false;
					});
			},
		},
		mounted() {
            this.fetchJobReasons()
            this.fetch();
		}
	};
</script>
